import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicHeader } from "../components/PublicHeader";
import { getPublicPayoutReceipts, type PublicPayoutReceipt } from "../lib/publicPayoutService";
import "../landing-final.css";

const ventures = ["INFERENCESAVER", "ICE FROM NORTH", "PIVORA", "KAVEX"];
const campaignNetwork = ["PRADA", "HUGO BOSS", "H&M", "VOGUE SCANDINAVIA"];
const workflow = [
  {
    step: "01",
    title: "Apply once",
    body: "Answer simple questions about your time, tools, and what tasks feel easiest. You join the platform, not one locked job title.",
  },
  {
    step: "02",
    title: "Get task access",
    body: "The system predicts the easiest first task type for you. A reviewer can adjust it after checking your answers.",
  },
  {
    step: "03",
    title: "Work the SOP",
    body: "Inside the member app, you get real product tasks, target cards, proof rules, review, and payouts only for approved output.",
  },
];

const taskCheckSignals = [
  "Can you follow a simple SOP?",
  "Can you follow exact proof rules?",
  "Can you work without spamming unqualified people?",
  "Can you finish small repeatable tasks cleanly?",
];

const placementSteps = [
  "You do not need to understand internal route names before applying.",
  "We score your answers for sales, research, content, community, and creator work.",
  "Your first placement is a starting lane, not a lifetime lock.",
  "Good proof, trust, and approved results unlock more task types.",
];

function formatPayout(receipt: PublicPayoutReceipt) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(receipt.amountUsd);
}

export function LandingPage() {
  const [payouts, setPayouts] = useState<PublicPayoutReceipt[]>([]);
  const [payoutsLoading, setPayoutsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void getPublicPayoutReceipts()
      .then((receipts) => {
        if (active) setPayouts(receipts);
      })
      .finally(() => {
        if (active) setPayoutsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="public-shell landing-final">
      <PublicHeader />
      <main id="main-content" className="lf-main">
        <section className="lf-hero" aria-labelledby="landing-title">
          <div className="lf-hero-copy">
            <p className="lf-kicker">MOMMI LIVE · MIDDLEMEN &amp; CO.</p>
            <h1 id="landing-title">Promote ready-made products.</h1>
            <p className="lf-lede">
              Apply for the platform. We find your strongest first lane. Follow SOPs, submit proof. Get paid for approved results.
            </p>
            <div className="lf-actions">
              <Link className="lf-button lf-button-primary" to="/apply">JOIN THE PLATFORM</Link>
              <a className="lf-text-link" href="#task-check">SEE HOW ENTRY WORKS</a>
            </div>
          </div>

          <aside className="lf-art-card" aria-label="Middlemen and Co platform preview">
            <div className="lf-bars" aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
              <i />
            </div>
            <div className="lf-plinth" aria-hidden="true" />
            <p>PRODUCT · SOP · PROOF · PAYOUT</p>
          </aside>
        </section>

        <section id="task-check" className="lf-section lf-audition" aria-labelledby="task-check-title">
          <div className="lf-section-heading">
            <div>
              <p className="lf-kicker">THE APPLICATION IS A TASK CHECK</p>
              <h2 id="task-check-title">We check if you can follow the SOP.</h2>
            </div>
            <p>Anyone can say they want to make money. The form checks if you can follow simple instructions, submit clean proof, and avoid spam.</p>
          </div>
          <div className="lf-signal-grid">
            {taskCheckSignals.map((item) => <article key={item}><span>CHECK</span><p>{item}</p></article>)}
          </div>
        </section>

        <section id="network" className="lf-brand-section" aria-label="Network signal">
          <div className="lf-section-heading compact">
            <div>
              <p className="lf-kicker">THE NETWORK IS THE ADVANTAGE</p>
              <h2>Products are ready. The work is distribution.</h2>
            </div>
            <p>Software, content, and operator-led ventures create the offers. The member app turns those offers into repeatable promotion, sales, research, and content tasks.</p>
          </div>
          <div className="lf-chip-row">
            {[...ventures, ...campaignNetwork].map((brand) => <span key={brand}>{brand}</span>)}
          </div>
        </section>

        <section className="lf-section lf-workflow" aria-labelledby="workflow-title">
          <div className="lf-section-heading">
            <h2 id="workflow-title">Simple entry. Clear payout rules.</h2>
            <p>The platform gives you the product, target customer, SOP, proof requirement, and payout model before you accept work.</p>
          </div>
          <div className="lf-card-grid">
            {workflow.map((item) => (
              <article key={item.step}>
                <span>{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="placement" className="lf-section lf-placement" aria-labelledby="placement-title">
          <div>
            <p className="lf-kicker">PLACEMENT IS PREDICTIVE</p>
            <h2 id="placement-title">You are not stuck forever.</h2>
          </div>
          <div className="lf-placement-list">
            {placementSteps.map((item, index) => <article key={item}><span>0{index + 1}</span><p>{item}</p></article>)}
          </div>
        </section>

        <section id="mommi" className="lf-section lf-founder" aria-labelledby="mommi-title">
          <div>
            <p className="lf-kicker">MEET THE MAN BEHIND THE NETWORK</p>
            <h2 id="mommi-title">Mommi Live turns connections into outcomes.</h2>
          </div>
          <div className="lf-founder-body">
            <img src="/mommi-live.jpg" alt="Mommi Live" />
            <div>
              <p>
                A Norwegian operator working across fashion, software, automotive, and digital ventures. Middlemen &amp; Co.
                is the member platform built around that deal flow: ready-made products, clear SOPs, proof, review, and payouts.
              </p>
              <div className="lf-founder-links">
                <a href="https://mommilive.carrd.co/" target="_blank" rel="noreferrer">PUBLIC PROFILE</a>
                <a href="https://shows.acast.com/densannepodden/episodes/658182ac63a0ac00171d1c65" target="_blank" rel="noreferrer">STORY</a>
              </div>
            </div>
          </div>
        </section>

        <section id="payouts" className="lf-section lf-payouts" aria-labelledby="payouts-title">
          <div className="lf-payout-heading">
            <p className="lf-kicker">ANONYMOUS · VERIFIED · COMPLETED</p>
            <h2 id="payouts-title">Payouts. Payouts. Payouts.</h2>
            <p>Only real paid records are published as anonymous receipts. No names shown.</p>
          </div>

          <div className="lf-payout-grid" aria-live="polite">
            {payoutsLoading ? (
              Array.from({ length: 6 }, (_, index) => (
                <div className="lf-payout-skeleton" key={index} aria-hidden="true" />
              ))
            ) : payouts.length > 0 ? (
              payouts.map((receipt) => (
                <article key={receipt.id}>
                  <span>PAID</span>
                  <strong>{formatPayout(receipt)}</strong>
                  <small>{receipt.method} · {new Date(receipt.paidAt).toLocaleDateString("en-GB")}</small>
                </article>
              ))
            ) : (
              <div className="lf-payout-empty">
                <span>VERIFIED FEED</span>
                <strong>Verified completed receipts publish here.</strong>
                <p>First completed payouts will appear automatically. Member identities stay private.</p>
              </div>
            )}
          </div>
        </section>

        <section className="lf-final-cta" aria-labelledby="final-cta-title">
          <div>
            <p className="lf-kicker">JOIN THE PLATFORM FIRST</p>
            <h2 id="final-cta-title">Get tasks. Follow the SOP. Prove the result.</h2>
          </div>
          <Link className="lf-button lf-button-primary" to="/apply">JOIN THE PLATFORM</Link>
        </section>

        <footer className="lf-footer">
          <span>© {new Date().getFullYear()} MIDDLEMEN &amp; CO.</span>
          <nav aria-label="Legal">
            <Link to="/legal/privacy">Privacy</Link>
            <Link to="/legal/terms">Terms</Link>
            <Link to="/legal/contractor">Contractor terms</Link>
            <Link to="/data-request/export">Export data</Link>
            <Link to="/data-request/deletion">Delete data</Link>
          </nav>
        </footer>
      </main>
    </div>
  );
}
